import React, { useEffect } from 'react';
import { useMemoryGame } from './MemoryGameContext';
import { MemoryObject, GRID_SIZE } from './types';

interface CellProps {
  x: number;
  y: number;
  object?: MemoryObject;
  onDrop?: (x: number, y: number) => void;
  isTarget?: boolean;
}

const Cell: React.FC<CellProps> = ({ x, y, object, onDrop, isTarget }) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const objectId = e.dataTransfer.getData('text/plain');
    onDrop?.(x, y);
  };

  const handleTouch = (e: React.TouchEvent) => {
    if (isTarget && onDrop) {
      e.preventDefault();
      onDrop(x, y);
    }
  };

  return (
    <div
      className={`
        w-8 h-8 xs:w-10 xs:h-10 sm:w-16 sm:h-16 lg:w-20 lg:h-20 border border-gray-200 flex items-center justify-center
        ${isTarget ? 'bg-blue-50 cursor-pointer' : 'bg-white'}
        transition-colors duration-200
      `}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onTouchEnd={handleTouch}
      onClick={isTarget ? () => onDrop?.(x, y) : undefined}
    >
      {object && (
        <div
          className="w-6 h-6 xs:w-8 xs:h-8 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg flex items-center justify-center text-sm xs:text-base sm:text-2xl lg:text-3xl cursor-move select-none"
          style={{ backgroundColor: object.color }}
          draggable={true}
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', object.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
        >
          {object.type}
        </div>
      )}
    </div>
  );
};

export const GameBoard: React.FC = () => {
  const { state, dispatch } = useMemoryGame();
  const { phase, objects, playerPlacements, timeLeft } = state;

  useEffect(() => {
    if (phase === 'memorization' && timeLeft > 0) {
      const timer = setInterval(() => {
        dispatch({ type: 'UPDATE_TIME', payload: timeLeft - 1 });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [phase, timeLeft, dispatch]);

  const handleDrop = (x: number, y: number) => {
    if (phase !== 'recall') return;

    // Check if there's already an object in this position
    const existingObject = playerPlacements.find(
      obj => obj.position.x === x && obj.position.y === y
    );
    if (existingObject) return; // Don't allow dropping if position is occupied

    // Find the next object to place
    const nextObjectType = objects[playerPlacements.length].type;
    
    dispatch({
      type: 'PLACE_OBJECT',
      payload: {
        id: `placed-${playerPlacements.length}`,
        type: nextObjectType,
        position: { x, y },
        color: objects[playerPlacements.length].color,
      },
    });
  };

  const getDisplayObjects = () => {
    if (phase === 'memorization') return objects;
    if (phase === 'recall') return playerPlacements;
    return [];
  };

  return (
    <div className="flex flex-col items-center space-y-6">
      {/* Timer */}
      {phase === 'memorization' && (
        <div className="text-base xs:text-lg sm:text-2xl font-bold text-blue-600">
          Memorize: {timeLeft} seconds
        </div>
      )}

      {/* Game Grid */}
      <div className="flex justify-center">
        <div 
          className="grid gap-0.5 sm:gap-1 p-1 sm:p-4 bg-gray-100 rounded-lg shadow-inner max-w-fit"
          style={{
            gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, index) => {
            const x = index % GRID_SIZE;
            const y = Math.floor(index / GRID_SIZE);
            const object = getDisplayObjects().find(
              (obj: MemoryObject) => obj.position.x === x && obj.position.y === y
            );

            return (
              <Cell
                key={`${x}-${y}`}
                x={x}
                y={y}
                object={object}
                onDrop={handleDrop}
                isTarget={phase === 'recall' && !object}
              />
            );
          })}
        </div>
      </div>

      {/* Object Bank for Recall Phase */}
      {phase === 'recall' && (
        <div className="flex justify-center">
          <div className="flex flex-col items-center space-y-2 xs:space-y-3">
            <p className="text-xs xs:text-sm sm:text-base font-medium text-gray-700">
              Tap a cell to place this object:
            </p>
            <div className="flex space-x-2 sm:space-x-4 p-3 sm:p-4 bg-white rounded-lg shadow">
              {objects.length > playerPlacements.length && (
                <div
                  key={objects[playerPlacements.length].id}
                  className="w-8 h-8 xs:w-10 xs:h-10 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center text-sm xs:text-base sm:text-2xl lg:text-3xl cursor-move select-none"
                  style={{ backgroundColor: objects[playerPlacements.length].color }}
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', objects[playerPlacements.length].id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  {objects[playerPlacements.length].type}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};